/**
 * Histórico do veículo — linha do tempo com o que o SIAG sabe da placa:
 * checklists, ocorrências, O.S. do SIAG, OS do ERP e movimentos de pneu.
 *
 * Cada evento diz de onde veio. A OS do ERP (oficina própria ou terceiro) e a
 * O.S. do SIAG são coisas diferentes e aparecem com rótulos diferentes.
 */
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import type { MaintenanceHistoryEventDto, MaintenanceVehicleHistoryDto } from "@bi/types";
import { api, ApiError } from "../../lib/api";
import { fmtNumber } from "../../lib/format";
import { EmptyState } from "../../components/EmptyState";
import { IconClipboard, IconShield, IconTire, IconTruck, IconWrench } from "../../components/icons";
import { CRITICALITY } from "../../features/maintenance/labels";
import { portalPaths } from "../../features/portal/portal-nav";

const ICONE: Record<MaintenanceHistoryEventDto["type"], React.ComponentType<{ className?: string }>> = {
  checklist: IconClipboard,
  non_conformity: IconShield,
  work_order: IconWrench,
  erp_order: IconWrench,
  tire: IconTire,
};

const COR: Record<MaintenanceHistoryEventDto["type"], string> = {
  checklist: "bg-tm-50 text-tm-600",
  non_conformity: "bg-danger-50 text-danger-600",
  work_order: "bg-warning-50 text-warning-700",
  erp_order: "bg-surface-muted text-gray-500",
  tire: "bg-info-50 text-info-600",
};

export function PortalVehicleHistoryPage() {
  const { plate = "" } = useParams();
  const { data, isLoading, error } = useQuery({
    queryKey: ["maintenance", "vehicle-history", plate],
    queryFn: () => api.get<MaintenanceVehicleHistoryDto>(`/maintenance/vehicles/${encodeURIComponent(plate)}/history`),
    retry: false,
  });

  if (error) {
    return (
      <EmptyState
        icon={<IconTruck className="w-6 h-6" />}
        title="Placa não encontrada"
        description={error instanceof ApiError ? error.message : "Não foi possível carregar o histórico."}
        action={<Link className="btn-secondary" to={portalPaths.vehicles}>Buscar outra placa</Link>}
      />
    );
  }

  const v = data?.vehicle;

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold text-gray-900 font-mono">{plate.toUpperCase()}</h1>
        <p className="text-sm text-gray-500">
          {[v?.brand, v?.vehicleTypeName].filter(Boolean).join(" · ") || "tipo não informado no cadastro"}
          {v?.lastOdometer ? ` · último KM ${fmtNumber(v.lastOdometer.km)} (${v.lastOdometer.sourceLabel})` : ""}
        </p>
      </div>

      {data && (
        <div className="grid grid-cols-3 gap-2 text-center">
          {[
            { label: "Ocorrências", valor: data.pending.openNonConformities, tom: data.pending.openNonConformities > 0 ? "text-danger-600" : "text-gray-900" },
            { label: "O.S. abertas", valor: data.pending.openWorkOrders, tom: data.pending.openWorkOrders > 0 ? "text-warning-700" : "text-gray-900" },
            { label: "Pneus críticos", valor: data.tires.critical.length, tom: data.tires.critical.length > 0 ? "text-danger-600" : "text-gray-900" },
          ].map((c) => (
            <div key={c.label} className="card p-3">
              <p className={`text-xl font-semibold ${c.tom}`}>{fmtNumber(c.valor)}</p>
              <p className="text-xs text-gray-500">{c.label}</p>
            </div>
          ))}
        </div>
      )}

      <div className="flex gap-2">
        <Link className="btn-secondary min-h-[44px]" to={`${portalPaths.checklists}?plate=${encodeURIComponent(plate)}`}>Checklists</Link>
        <Link className="btn-secondary min-h-[44px]" to={`${portalPaths.occurrences}?plate=${encodeURIComponent(plate)}&status=`}>Ocorrências</Link>
      </div>

      {isLoading ? (
        <p className="text-sm text-gray-500">Carregando…</p>
      ) : (data?.events.length ?? 0) === 0 ? (
        <div className="card">
          <EmptyState icon={<IconClipboard className="w-6 h-6" />} title="Sem histórico registrado"
            description="Checklists, ocorrências, O.S. e manutenções desta placa aparecem aqui." />
        </div>
      ) : (
        <ol className="space-y-2">
          {data!.events.map((e) => {
            const Icone = ICONE[e.type];
            const conteudo = (
              <div className="card p-3 flex gap-3 items-start">
                <div className={`w-8 h-8 rounded-lg grid place-items-center flex-shrink-0 ${COR[e.type]}`}>
                  <Icone className="w-4 h-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-2">
                    <p className="text-sm font-medium text-gray-900">{e.title}</p>
                    <time className="text-xs text-gray-400 flex-shrink-0">{new Date(e.at).toLocaleDateString("pt-BR")}</time>
                  </div>
                  {e.detail && <p className="text-xs text-gray-500 mt-0.5">{e.detail}</p>}
                  {e.severity && (
                    <span className={`${CRITICALITY[e.severity as keyof typeof CRITICALITY]?.badge ?? "badge-gray"} mt-1`}>
                      {CRITICALITY[e.severity as keyof typeof CRITICALITY]?.label ?? e.severity}
                    </span>
                  )}
                </div>
              </div>
            );
            return <li key={`${e.type}-${e.id}`}>{e.link ? <Link to={e.link}>{conteudo}</Link> : conteudo}</li>;
          })}
        </ol>
      )}
    </div>
  );
}
